/**
 * Типы раздела «Лиды» (воронки Avito и СберАвто).
 *
 * Источник правды — миграция `supabase/migrations/20260526170000_inspection_leads.sql`
 * в репо `stryzh`. INSERT в БД делает только edge function avito-lead под
 * service_role; UPDATE доступен admin/dispatcher (RLS через
 * public.is_admin_or_dispatcher()). Изменения автоматически логируются в
 * `public.inspection_lead_changes` триггерами.
 */

/** Статусы лида обеих воронок. Какие допустимы в какой воронке — CHECK в БД
 *  и `LEAD_PIPELINE_UI[pipeline].statuses` здесь. Avito (Волна E, 2026-05-29):
 *  инспекционная часть переехала в заказ (orders.status), статусы лида ведут
 *  только до оплаты; 'paid' ставит webhook, 'cancelled' с refunded_at = возврат. */
export type InspectionLeadStatus =
  | 'new'
  | 'in_progress'
  | 'awaiting_payment'
  | 'paid'
  | 'cancelled'
  // Воронка СберАвто (миграция stryzh 20261030120000)
  | 'oo_review'
  | 'order_created'
  | 'lot_ready'
  | 'auction'
  | 'approval'
  | 'done'
  // Лот Carlink закрылся FAILED: продажа не состоялась (миграция stryzh 20261103180000)
  | 'not_sold'

/** Воронка лида (`inspection_leads.pipeline`). avito — Avito, лендинг и
 *  ручные лиды (клиент платит сам); sberauto — трейд-ин СберАвто. Набор
 *  допустимых статусов у воронок разный, это проверяет CHECK в БД. */
export type LeadPipeline = 'avito' | 'sberauto'

/** Статусы воронки Avito по порядку. */
export const LEAD_STATUS_ORDER: InspectionLeadStatus[] = [
  'new',
  'in_progress',
  'awaiting_payment',
  'paid',
  'cancelled',
]

/** Статусы воронки СберАвто по порядку. */
export const SBER_LEAD_STATUS_ORDER: InspectionLeadStatus[] = [
  'new',
  'in_progress',
  'oo_review',
  'order_created',
  'lot_ready',
  'auction',
  'approval',
  'done',
  'not_sold',
  'cancelled',
]

export function leadStatusOrder(pipeline: LeadPipeline): InspectionLeadStatus[] {
  return pipeline === 'sberauto' ? SBER_LEAD_STATUS_ORDER : LEAD_STATUS_ORDER
}

/** Позиция статуса в воронке лида — для сортировки таблицы. */
export function leadStatusRank(status: InspectionLeadStatus, pipeline: LeadPipeline): number {
  return leadStatusOrder(pipeline).indexOf(status)
}

/** Ключ подписи статуса/стадии: у воронки может быть своя формулировка
 *  (`leads.status.sberauto.in_progress` = «В работе ФО»), иначе общая. */
export function leadStatusLabelKey(
  kind: 'status' | 'stage',
  value: string,
  pipeline: LeadPipeline | null | undefined,
  exists: (key: string) => boolean,
): string {
  const own = `leads.${kind}.${pipeline ?? ''}.${value}`
  return pipeline && exists(own) ? own : `leads.${kind}.${value}`
}

/**
 * Синтетический стейдж воронки лида — производный из (lead.status,
 * orders.status). В БД не хранится: добавляет к raw-статусам Avito ещё одну
 * визуальную ступень — «Успешно» (оплачен + связанный заказ COMPLETED), —
 * чтобы в канбане отделять «в работе» от «уже сделано». Источник правды —
 * по-прежнему lead.status; стейдж считается на фронте.
 *
 * Маппинг (`deriveLeadStage`):
 *  - lead.cancelled → 'cancelled'
 *  - lead.paid + order COMPLETED → 'completed'
 *  - иначе → совпадает с lead.status
 *
 * Намеренно НЕ переводим в 'cancelled' заказы в REFUSED_* и CANCELLED, пока
 * лид сам не перешёл в 'cancelled': для оператора это «оплачено, разбираемся,
 * возможен возврат», и в воронке логичнее держать на «Оплачен» до фактической
 * отмены лида (возврат — отдельный шаг).
 */
export type InspectionLeadStage =
  | 'new'
  | 'in_progress'
  | 'awaiting_payment'
  | 'paid'
  | 'completed'
  | 'cancelled'
  | 'oo_review'
  | 'order_created'
  | 'lot_ready'
  | 'auction'
  | 'approval'
  | 'done'
  | 'not_sold'

export const LEAD_STAGE_ORDER: InspectionLeadStage[] = [
  'new',
  'in_progress',
  'awaiting_payment',
  'paid',
  'completed',
  'cancelled',
]

/** Колонки канбана СберАвто: стадия = статус лида. Ход осмотра виден по
 *  бейджу статуса заказа на карточке (отдельных этапов не заводим). */
export const SBER_LEAD_STAGE_ORDER: InspectionLeadStage[] = [
  'new',
  'in_progress',
  'oo_review',
  'order_created',
  'lot_ready',
  'auction',
  'approval',
  'done',
  'not_sold',
  'cancelled',
]

export function leadStageOrder(pipeline: LeadPipeline): InspectionLeadStage[] {
  return pipeline === 'sberauto' ? SBER_LEAD_STAGE_ORDER : LEAD_STAGE_ORDER
}

/**
 * Что умеет воронка в консоли. Зеркало таблицы `lead_pipelines` в БД
 * (client_sms / landing / payment / autoteka_* / terminal_statuses) плюс
 * чисто интерфейсные вещи (какие секции карточки показывать). Единственное
 * место, где консоль различает воронки: компоненты читают флаги, а не
 * сравнивают `pipeline === 'sberauto'`.
 */
export interface LeadPipelineUi {
  statuses: InspectionLeadStatus[]
  stages: InspectionLeadStage[]
  /** Нельзя взять в работу / отпустить. */
  terminal: InspectionLeadStatus[]
  /** Стадии с живым таймером «время в статусе» на канбане. */
  timerStatuses: InspectionLeadStatus[]
  /** Стадии, где карточка канбана показывает статус заказа; null — везде, где заказ есть. */
  orderBadgeStatuses: InspectionLeadStatus[] | null
  /** Стадии, где карточка и таблица показывают лот Carlink («не создан», черновик, торги…). */
  lotBadgeStatuses: InspectionLeadStatus[]
  /** Стадии, где карточка канбана прячет адрес (он уже не нужен); null — показывать всегда. */
  cardLocationHiddenStatuses: InspectionLeadStatus[] | null
  /** SMS клиенту (форма, статус, отчёт). */
  clientSms: boolean
  /** Публичный лендинг по landing_token («Глазами клиента»). */
  landing: boolean
  /** Клиент платит за осмотр сам (цена, карточка платежа, заказ после оплаты). */
  payment: boolean
  /** Статус, с которого разрешён (платный) запрос Автотеки; null — с любого. */
  autotekaFromStatus: InspectionLeadStatus | null
  /** Статус, при переходе в который БД сама запрашивает Автотеку
   *  (lead_pipelines.autoteka_auto_on_status); null: только кнопкой. */
  autotekaAutoOnStatus: InspectionLeadStatus | null
  /** Заказ создаётся вручную с этого статуса и требует адреса осмотра; null —
   *  заказ по оплате (Avito). */
  orderFromStatus: InspectionLeadStatus | null
  /** После создания заказа этап лида продолжает меняться (торги и т. п.). */
  statusEditableAfterOrder: boolean
  /** Статусы до / после заказа — для выпадающего списка этапов. null — все. */
  statusesBeforeOrder: InspectionLeadStatus[] | null
  statusesAfterOrder: InspectionLeadStatus[] | null
  /** Стадии, на которых от команды действий нет: ход у внешней стороны
   *  (Carlink, СберАвто, клиент) либо этап конечный. На канбане точка. */
  idleStages: InspectionLeadStage[]
  /** Результат звонка ФО (lead_pipelines.call_results): выбор в палитре,
   *  «не дозвонились» по звонкам Mango, показ в кабинете партнёра. */
  callResults: boolean
  sections: {
    itemId: boolean
    sellerAd: boolean
    price: boolean
    payment: boolean
    consents: boolean
    avitoApi: boolean
    partner: boolean
    carlink: boolean
  }
}

export const LEAD_PIPELINE_UI: Record<LeadPipeline, LeadPipelineUi> = {
  avito: {
    statuses: LEAD_STATUS_ORDER,
    stages: LEAD_STAGE_ORDER,
    terminal: ['paid', 'cancelled'],
    timerStatuses: ['new', 'in_progress', 'awaiting_payment'],
    orderBadgeStatuses: null,
    lotBadgeStatuses: [],
    cardLocationHiddenStatuses: null,
    clientSms: true,
    landing: true,
    payment: true,
    autotekaFromStatus: null,
    autotekaAutoOnStatus: null,
    orderFromStatus: null,
    statusEditableAfterOrder: false,
    statusesBeforeOrder: null,
    statusesAfterOrder: null,
    idleStages: ['awaiting_payment', 'completed', 'cancelled'],
    callResults: false,
    sections: {
      itemId: true, sellerAd: true, price: true, payment: true, consents: true, avitoApi: true,
      partner: false, carlink: false,
    },
  },
  sberauto: {
    statuses: SBER_LEAD_STATUS_ORDER,
    stages: SBER_LEAD_STAGE_ORDER,
    terminal: ['done', 'not_sold', 'cancelled'],
    timerStatuses: ['new', 'in_progress', 'oo_review', 'order_created', 'lot_ready', 'auction', 'approval'],
    // От «Готов к торгам» заказ всегда завершён (условие этапа), бейдж там шум.
    orderBadgeStatuses: ['order_created'],
    lotBadgeStatuses: ['lot_ready', 'auction', 'approval', 'done', 'not_sold'],
    cardLocationHiddenStatuses: ['lot_ready', 'auction', 'approval', 'done', 'not_sold', 'cancelled'],
    clientSms: false,
    landing: false,
    payment: false,
    autotekaFromStatus: 'oo_review',
    autotekaAutoOnStatus: 'oo_review',
    orderFromStatus: 'oo_review',
    statusEditableAfterOrder: true,
    // «Заказ создан» ставит только создание заказа; до заказа — этапы ФО/ОО,
    // после — торги и завершение.
    statusesBeforeOrder: ['new', 'in_progress', 'oo_review'],
    statusesAfterOrder: ['order_created', 'lot_ready', 'auction', 'approval', 'done', 'not_sold'],
    idleStages: ['lot_ready', 'auction', 'approval', 'done', 'not_sold', 'cancelled'],
    callResults: true,
    sections: {
      itemId: false, sellerAd: false, price: false, payment: false, consents: false, avitoApi: false,
      partner: true, carlink: true,
    },
  },
}

export function leadPipelineUi(pipeline: LeadPipeline): LeadPipelineUi {
  return LEAD_PIPELINE_UI[pipeline] ?? LEAD_PIPELINE_UI.avito
}

/** Этапы, доступные в выпадающем списке карточки: текущий, отмена и
 *  «своя половина» воронки относительно заказа. */
export function leadStatusOptions(lead: {
  pipeline: LeadPipeline
  status: InspectionLeadStatus
  order_id: string | null
}): InspectionLeadStatus[] {
  const ui = leadPipelineUi(lead.pipeline)
  const half = lead.order_id ? ui.statusesAfterOrder : ui.statusesBeforeOrder
  if (!half) return ui.statuses
  return ui.statuses.filter((s) => s === lead.status || s === 'cancelled' || half.includes(s))
}


export function deriveLeadStage(
  leadStatus: InspectionLeadStatus,
  orderStatus: string | null | undefined,
): InspectionLeadStage {
  if (leadStatus === 'cancelled') return 'cancelled'
  if (leadStatus === 'paid' && orderStatus === 'COMPLETED') return 'completed'
  return leadStatus
}

/** Источник изменения поля. Совпадает с CHECK на inspection_lead_changes.source. */
export type InspectionLeadChangeSource =
  | 'avito_webhook'
  | 'autoteka'
  | 'landing_form'
  | 'dispatcher'
  | 'system'

/** Клиентское событие на лиде (public.lead_events). UI: третий источник
 *  в разделе «Коммуникации» наряду со звонками и SMS. Источники:
 *  - form_opened / status_page_viewed — beacon с лендинга через edge
 *    landing-track-event (дедуп 30 сек).
 *  - form_submitted / payment_started / payment_completed — триггеры на
 *    inspection_leads (по полям pd_consent_at / tbank_payment_id /
 *    payment_at). Подробнее — миграция stryzh 20260620080000. */
export interface LeadEvent {
  id: string
  lead_id: string
  type:
    | 'form_opened'
    | 'form_submitted'
    | 'payment_started'
    | 'payment_completed'
    | 'payment_refunded'
    | 'status_page_viewed'
  at: string
  /** Сотрудник, выполнивший событие (только для payment_refunded из админки).
   *  NULL — клиентское событие или авто-webhook. */
  actor_id?: string | null
  /** ФИО актора (read-through из profiles, заполняется в listLeadEventsByLead). */
  actor_name?: string | null
}

/** Исходящее SMS клиенту (public.sms_outbox). Пишется edge send-sms на
 *  каждую отправку (миграция stryzh 20260619180000). UI: раздел
 *  «Коммуникации» в карточке лида показывает SMS вместе со звонками. */
export interface LeadSms {
  id: string
  phone: string
  message: string
  /** skipped: отправка выключена настройкой, строка только для журнала
   *  (SMS дилерам о старте торгов при sms_auction_start_enabled=false). */
  status: 'sent' | 'failed' | 'skipped'
  /** auction_start: SMS дилерам о старте торгов (не клиенту, телефон чужой);
   *  auction_test: то же, тест из карточки лида тестовым контактам. */
  source: 'manual' | 'awaiting_payment' | 'paid' | 'report' | 'assigned' | 'auction_start' | 'auction_test' | 'other'
  sent_at: string
  lead_id: string | null
  /** Диспетчер, нажавший «Отправить SMS» (только source='manual'). */
  actor_id?: string | null
  /** ФИО актора (read-through из profiles, заполняется в listSmsByClientPhone). */
  actor_name?: string | null
}

/** Звонок Mango Office (public.mango_calls). Метаданные — mango-sync-calls,
 *  транскрипция/ссылка на запись — лениво через mango-call-detail. */
export interface MangoCall {
  id: string
  direction: 'incoming' | 'outgoing' | 'unknown'
  client_phone: string | null
  manager_name: string | null
  started_at: string
  duration_sec: number
  recording_ids: string[]
  /** Сводка звонка Carlink AI по расшифровке (mango_calls.summary): одна фраза,
   *  строка под звонком в «Коммуникациях». null: сводки ещё нет или расшифровка пустая. */
  summary?: string | null
}

/** Реплика транскрипции (agent — менеджер, client — клиент). */
export interface MangoTranscriptPhrase {
  role: 'agent' | 'client'
  text: string
}

export interface MangoCallDetail {
  status: string
  transcript: MangoTranscriptPhrase[]
  recordingUrl: string | null
  transcriptFromCache?: boolean
}

export interface InspectionLead {
  id: string
  /** Наш номер заявки внутри воронки: 1, 2, 3… (триггер БД при создании). */
  lead_no: number
  status: InspectionLeadStatus
  /** Воронка. Существующие лиды — 'avito' (дефолт БД). */
  pipeline: LeadPipeline
  source: string
  client_name: string | null
  client_phone: string | null
  location: string | null
  vehicle_brand: string | null
  vehicle_model: string | null
  vehicle_year: number | null
  vehicle_vin: string | null
  vehicle_license_plate: string | null
  seller_name: string | null
  seller_phone: string | null
  item_link: string | null
  item_id: number | null
  /** Комментарий клиента к заявке (форма лендинга, необязательный). */
  client_comment: string | null
  /** Ссылка на онлайн-отчёт Автотеки (webLink) — публичная веб-версия. */
  autoteka_web_url: string | null
  /** Номер купленного отчёта Автотеки — по нему скачивается PDF (autoteka-report-pdf). */
  autoteka_report_id?: number | string | null
  /** PDF отчёта Автотеки в bucket autoteka-reports (выгружается в фоне). */
  autoteka_pdf_path?: string | null
  /** PDF оценки CM Expert (bucket cm-expert-reports), одна актуальная. */
  cm_expert_pdf_path?: string | null
  cm_expert_pdf_at?: string | null
  payment_amount: number | null
  payment_at: string | null
  /** OrderId, переданный в /v2/Init Т-Банка. Формат "lead-<12hex>". */
  tbank_order_id: string | null
  /** PaymentId от Т-Банка (нужен для возврата через /v2/Cancel). */
  tbank_payment_id: string | null
  /** Дата возврата платежа. Заполняется webhook'ом REFUNDED или edge function
   *  tbank-refund-payment. После установки — immutable (БД-триггер). */
  refunded_at: string | null
  /** Момент РЕАЛЬНОГО списания (двухстадийка: Confirm → CONFIRMED, при
   *  завершении осмотра). NULL = деньги в резерве (холд, AUTHORIZED). */
  tbank_captured_at: string | null
  /** Время последней неуспешной попытки Confirm (холд мог сгореть) — для
   *  бейджа «зачисление не прошло». NULL = ошибок не было. */
  tbank_capture_failed_at: string | null
  /** Токен для публичной ссылки на лендинг avs.carlink.ru. NULL в terminal-статусах. */
  landing_token: string | null
  /** Момент согласия клиента на ПДн (ставится формой лендинга). */
  pd_consent_at: string | null
  /** Момент согласия клиента с офертой. */
  offer_consent_at: string | null
  /** B2C: котируемая цена осмотра, ₽ («сколько просим»). Дефолт 4990; правит
   *  диспетчер до оплаты. Источник суммы для tbank-init-payment. */
  price_amount: number
  /** Заказ, созданный из лида при конвертации (back-reference). NULL до конвертации. */
  order_id: string | null
  /** Оператор, взявший лид в работу (claim ownership). NULL = свободен. */
  assigned_to: string | null
  /** Момент claim (NULL если assigned_to=NULL). */
  assigned_at: string | null
  /** Когда автомат отправил клиенту SMS со ссылкой на форму (первый переход
   *  в awaiting_payment). Защита от повторной отправки. */
  awaiting_payment_sms_sent_at: string | null
  /** Когда клиенту ушла авто-SMS «отчёт готов» (заказ → COMPLETED). NULL —
   *  ещё не отправляли. */
  report_sms_sent_at: string | null
  /** id контакта в respond.io (Carlink), если клиент найден по телефону.
   *  NULL = не найден или ещё не проверяли. Заполняет edge respond-lookup-contact. */
  respond_contact_id: number | null
  /** Тестовый лид: SMS дилерам только тестовым получателям, без Telegram,
   *  не виден партнёру. Тестовым считается и лид с маркой на «тест» (lead_is_test в БД). */
  is_test?: boolean
  created_at: string
  updated_at: string
  /** Момент входа в текущий status (БД-триггер). Точка отсчёта таймера на канбане. */
  status_changed_at: string
  /** Структурная причина отмены (контролируемый словарь). NULL — не отменён/не указана. */
  cancel_reason: CancelReasonCode | null
  /** Причина отмены свободным текстом (СберАвто). */
  cancel_comment: string | null
  /** Осмотр не проведён: заказ закрыт без акта (отмена / отказ). Очищается при новом заказе. */
  inspection_failed_at?: string | null
  /** Причина: текст диспетчера из отмены заказа или фиксированная для отказов. */
  inspection_failed_reason?: string | null
  /** Комментарий для партнёра о текущем состоянии (одно поле, перезаписывается). */
  partner_comment?: string | null
  /** Когда partner_comment менялся последний раз (триггер БД). */
  partner_comment_at?: string | null
  // ── СберАвто ──
  /** Номер клиента в CRM СберАвто. Уникален в воронке; повторная анкета
   *  с тем же номером дополняет лид. */
  partner_external_id: string | null
  /** СберАвто: ФО связался с клиентом (любой результат звонка «Дозвонились»). */
  contacted_at?: string | null
  /** Текущий результат звонка ФО (= CHECK inspection_leads.call_result). */
  call_result?: CallResult | null
  /** «Комментарий для СберАвто» написал Carlink AI (правка сотрудником сбрасывает). */
  partner_comment_by_ai?: boolean
  call_result_at?: string | null
  /** Номер попытки у «не дозвонились» (недозвоны подряд после разговора). */
  call_result_attempt?: number | null
  /** «Перезвонить позже»: когда. */
  callback_at?: string | null
  /** Отвеченный звонок Mango без результата ФО (последний): по нему палитра
   *  показывает «Как прошёл звонок?». Снимается любым результатом. */
  pending_call_id?: string | null
  pending_call_at?: string | null
  /** Новый автомобиль, который клиент берёт в зачёт трейд-ина. */
  trade_in_target_vehicle: string | null
  /** Исходные сообщения СберАвто дословно, по одному на каждую вставку анкеты. */
  partner_messages?: { at: string; by: string | null; text: string }[]
  /** Адрес осмотра. */
  inspection_address: string | null
  /** Заметки квалификации ФО (стоп-факторы, ключи, ПТС…). */
  qualification_note: string | null
  /** Запрос Автотеки по VIN/госномеру (СберАвто). NULL — не запрашивали. */
  autoteka_state: 'preview_pending' | 'report_pending' | 'done' | 'failed' | null
  autoteka_requested_at: string | null
  autoteka_error: string | null
}

/** Контролируемый словарь причин отмены (= CHECK на inspection_leads.cancel_reason).
 *  Порядок = порядок в дропдауне окна отмены. */
export const CANCEL_REASON_CODES = [
  'unreachable',
  'no_payment',
  'service_mismatch',
  'changed_mind',
  'object_gone',
  'not_target',
  'price_objection',
  'paid_then_refund',
  'unknown',
] as const
/** Причины, которые ставит результат звонка (в окне отмены их нет). */
export type CancelReasonCode = (typeof CANCEL_REASON_CODES)[number] | 'client_refused' | 'stop_factor'

/** Результат звонка ФО. Порядок = порядок в списке выбора. */
/** Тестовый лид: галочка is_test или марка на «тест» (как lead_is_test в БД). */
export function leadIsTest(lead: { is_test?: boolean | null; vehicle_brand?: string | null }): boolean {
  return Boolean(lead.is_test) || /^\s*тест/i.test(lead.vehicle_brand ?? '')
}

export const CALL_RESULTS_REACHED = ['handed_to_oo', 'callback', 'wrong_number', 'refused', 'stop_factor'] as const
export const CALL_RESULTS_NOT_REACHED = ['no_answer', 'unreachable'] as const
export type CallResult =
  | (typeof CALL_RESULTS_REACHED)[number]
  | (typeof CALL_RESULTS_NOT_REACHED)[number]
/** После стольких недозвонов результат «не дозвонились» итоговый. */
export const CALL_ATTEMPTS_FINAL = 3
/** Звонок без результата старше этого: напоминание (рамка блока в палитре,
 *  метка «нет результата звонка» на канбане). */
export const CALL_RESULT_REMIND_MS = 30 * 60_000

/** Строка списка/карточки — со статусом связанного заказа и именем оператора. */
export interface InspectionLeadRow extends InspectionLead {
  /** Статус связанного заказа (read-through), если лид сконвертирован. */
  order_status?: string | null
  /** URL отчёта об осмотре у связанного заказа (read-through,
   *  `orders.external_inspection_report_url`). Непусто = отчёт готов →
   *  кнопка «Отправить SMS с отчётом» активна. */
  order_report_url?: string | null
  /** Read-through: orders.inspection_scheduled_at связанного заказа. */
  order_inspection_at?: string | null
  /** Имя оператора, взявшего лид (read-through по assigned_to). */
  assigned_to_name?: string | null
  /** Статус лота Carlink (read-through по carlink_lots); null — лота нет. */
  lot_status?: string | null
}

export interface InspectionLeadChange {
  id: string
  lead_id: string
  field_name: string
  old_value: string | null
  new_value: string | null
  source: InspectionLeadChangeSource
  changed_by: string | null
  changed_at: string
}

/** Лента истории — с раскрытым именем автора, если он был. */
export interface InspectionLeadChangeRow extends InspectionLeadChange {
  changed_by_name: string | null
}

/**
 * Комментарий диспетчера на лиде (append-only лента). Источник правды —
 * миграция `20260604140000_inspection_lead_comments.sql` в репо `stryzh`.
 * Заменяет прежнее перезаписываемое поле `dispatcher_notes`. UPDATE/DELETE
 * запрещены RLS — история неизменна; автор проставляется БД из auth.uid().
 */
export interface InspectionLeadComment {
  id: string
  lead_id: string
  /** Автор. NULL у перенесённых из dispatcher_notes без известного автора. */
  author_id: string | null
  body: string
  created_at: string
  /** Раскрытое имя автора (read-through по author_id). */
  author_name: string | null
  /** note — заметка диспетчера; system — авто-эхо Carlink AI (напоминания). */
  kind?: 'note' | 'system'
}
